import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import CustomDateField from "../CustomDateField";
import CustomTextField from "../CustomTextField";
import CustomLabel from "../CustomLabel";
import { COLORS } from "../../assets/theme";
import {
  getCoachPayoutSessions,
  setCoachPayoutSessionAmount,
} from "../../services/CoachesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { CoachPayoutSession, CoachPayoutSessionsList } from "./types";
import { formatMoney, formatTime, isPayoutAmountSet } from "./classUtils";

interface CoachPayoutSessionsDialogProps {
  open: boolean;
  coachId: string | null;
  coachName?: string;
  onClose: () => void;
  /** Fired after any amount is saved, so a caller showing the payout report can refresh. */
  onChanged?: () => void;
}

const toInputDate = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const defaultRange = () => {
  const now = new Date();
  return {
    from: toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toInputDate(now),
  };
};

const SummaryTile: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      flex: "1 1 140px",
      border: "1px solid #eee",
      borderRadius: 2,
      p: 1.5,
      bgcolor: COLORS.MainBackground,
    }}
  >
    <Typography sx={{ fontSize: 12, color: "#666" }}>{label}</Typography>
    <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{value}</Typography>
  </Box>
);

/**
 * CUSTOM_PER_CLASS coaches have no fixed rate — each DONE session's payout is
 * entered here by hand. Re-saving a session's amount just updates it in
 * place, so this doubles as the edit flow, not just first entry.
 */
const CoachPayoutSessionsDialog: React.FC<CoachPayoutSessionsDialogProps> = ({
  open,
  coachId,
  coachName,
  onClose,
  onChanged,
}) => {
  const [range, setRange] = React.useState(defaultRange);
  const [data, setData] = React.useState<CoachPayoutSessionsList | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [amountInputs, setAmountInputs] = React.useState<Record<string, string>>(
    {}
  );
  const [savingId, setSavingId] = React.useState<string | null>(null);

  const fetchSessions = React.useCallback(
    async (from: string, to: string) => {
      if (!coachId) return;
      setLoading(true);
      try {
        const result = await getCoachPayoutSessions(coachId, { from, to });
        setData(result);
        setAmountInputs(
          Object.fromEntries(
            (result.sessions || []).map((s) => [
              s.appointment_id,
              s.amount !== null && s.amount !== undefined ? String(s.amount) : "",
            ])
          )
        );
      } catch (error) {
        toast.error(
          getErrorMessage(error, "Failed to load sessions needing amounts.")
        );
        setData(null);
        setAmountInputs({});
      } finally {
        setLoading(false);
      }
    },
    [coachId]
  );

  React.useEffect(() => {
    if (!open || !coachId) return;
    const initial = defaultRange();
    setRange(initial);
    fetchSessions(initial.from, initial.to);
  }, [open, coachId, fetchSessions]);

  const handleSave = async (session: CoachPayoutSession) => {
    if (!coachId) return;
    const raw = amountInputs[session.appointment_id];
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error("Enter a valid, non-negative amount.");
      return;
    }

    setSavingId(session.appointment_id);
    try {
      await setCoachPayoutSessionAmount(coachId, session.appointment_id, amount);
      toast.success("Amount saved.");
      await fetchSessions(range.from, range.to);
      onChanged?.();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save the amount."));
    } finally {
      setSavingId(null);
    }
  };

  const sessions = data?.sessions || [];
  // Derived from the rows themselves rather than trusted from the aggregate
  // fields, so the summary tiles can never disagree with what each row shows.
  const enteredCount = sessions.filter((s) => isPayoutAmountSet(s)).length;
  const pendingCount = sessions.length - enteredCount;
  const totalEnteredAmount = sessions.reduce(
    (sum, s) => (isPayoutAmountSet(s) ? sum + Number(s.amount) : sum),
    0
  );

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      paperSx={{ minWidth: { sm: 760 }, maxWidth: 960 }}
      title={`Payout Amounts — ${coachName || "Coach"}`}
      actions={<CustomButton label="Close" variant="outlined" onClick={onClose} />}
    >
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Box sx={{ flex: "1 1 180px" }}>
          <CustomLabel text="From" fontSize={14} fontWeight={500} />
          <CustomDateField
            value={range.from}
            onChange={(v: string) => setRange((prev) => ({ ...prev, from: v }))}
            fullWidth
          />
        </Box>
        <Box sx={{ flex: "1 1 180px" }}>
          <CustomLabel text="To" fontSize={14} fontWeight={500} />
          <CustomDateField
            value={range.to}
            onChange={(v: string) => setRange((prev) => ({ ...prev, to: v }))}
            fullWidth
          />
        </Box>
        <CustomButton
          label="Apply"
          variant="contained"
          bgColor={COLORS.MainColor}
          textColor={COLORS.WhiteText}
          disabled={loading}
          onClick={() => fetchSessions(range.from, range.to)}
          sx={{ mb: 0.5 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 2 }}>
            <SummaryTile label="Sessions" value={String(sessions.length)} />
            <SummaryTile label="Set" value={String(enteredCount)} />
            <SummaryTile label="Pending" value={String(pendingCount)} />
            <SummaryTile
              label="Total entered"
              value={formatMoney(totalEnteredAmount)}
            />
          </Box>

          <Box sx={{ mt: 3, overflowX: "auto" }}>
            {sessions.length === 0 ? (
              <Typography sx={{ color: "#777", fontSize: 14 }}>
                No completed sessions in this period yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Class</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ width: 160 }}>Amount</TableCell>
                    <TableCell align="right">Save</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.appointment_id}>
                      <TableCell>{session.class_name || "-"}</TableCell>
                      <TableCell>{formatTableDate(session.start_at)}</TableCell>
                      <TableCell>
                        {`${formatTime(session.start_at)} - ${formatTime(
                          session.end_at
                        )}`}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={isPayoutAmountSet(session) ? "Set" : "Pending"}
                          sx={{
                            bgcolor: isPayoutAmountSet(session)
                              ? "#e6f6ec"
                              : "#fbf0dd",
                            color: isPayoutAmountSet(session)
                              ? "#1e8a4c"
                              : "#b9791a",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <CustomTextField
                          type="number"
                          value={amountInputs[session.appointment_id] ?? ""}
                          onChange={(e: any) =>
                            setAmountInputs((prev) => ({
                              ...prev,
                              [session.appointment_id]: e.target.value,
                            }))
                          }
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Save amount">
                          <span>
                            <IconButton
                              size="small"
                              disabled={savingId === session.appointment_id}
                              onClick={() => handleSave(session)}
                            >
                              <SaveIcon
                                sx={{ color: COLORS.MainColor, width: 20 }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </>
      )}
    </CustomDialog>
  );
};

export default CoachPayoutSessionsDialog;
